import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import api from "../lib/api";

const RATE_FIELDS = {
  overnight: [
    ["dog", "Dog / night"],
    ["dog_additional", "Extra dog / night"],
    ["cat", "Cat / night"],
    ["cat_additional", "Extra cat / night"],
  ],
  daily: [
    ["dog", "Dog / day"],
    ["dog_additional", "Extra dog"],
    ["cat", "Cat / day"],
    ["cat_additional", "Extra cat"],
  ],
  walk: [
    ["walk_30", "30-min walk"],
    ["walk_60", "60-min walk"],
    ["dog_additional", "Extra dog"],
  ],
};

const fieldsFor = (svc) =>
  svc.slug === "dog-walking" ? RATE_FIELDS.walk : svc.overnight ? RATE_FIELDS.overnight : RATE_FIELDS.daily;

const AdminRates = () => {
  const [services, setServices] = useState(null);
  const [saving, setSaving] = useState("");

  useEffect(() => {
    api
      .get("/services")
      .then((r) => setServices(r.data))
      .catch(() => {
        toast.error("Could not load services — please refresh.");
        setServices([]);
      });
  }, []);

  const setRate = (slug, key, value) =>
    setServices((prev) =>
      prev.map((s) => (s.slug === slug ? { ...s, rates: { ...s.rates, [key]: value } } : s))
    );

  const setPrice = (slug, value) =>
    setServices((prev) => prev.map((s) => (s.slug === slug ? { ...s, price: value } : s)));

  const save = async (svc) => {
    setSaving(svc.slug);
    const rates = {};
    fieldsFor(svc).forEach(([key]) => {
      const n = Number(svc.rates[key]);
      rates[key] = Number.isFinite(n) && n >= 0 ? n : 0;
    });
    try {
      const { data } = await api.patch(`/admin/services/${svc.slug}`, {
        rates,
        price: (svc.price || "").trim(),
      });
      setServices((prev) =>
        prev.map((s) => (s.slug === svc.slug ? { ...s, price: data.price, rates: data.rates } : s))
      );
      toast.success(`${svc.name} rates saved — the booking calculator now uses them.`);
    } catch {
      toast.error(`Could not save ${svc.name} rates.`);
    } finally {
      setSaving("");
    }
  };

  return (
    <section
      data-testid="admin-rates"
      className="rounded-3xl border border-line bg-white p-6 shadow-[0_8px_30px_rgba(42,42,42,0.04)] md:p-8"
    >
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-marmalade">Service rates</p>
          <h3 className="mt-2 font-display text-2xl font-medium text-ink">Your prices</h3>
          <p className="mt-1 max-w-xl text-sm leading-relaxed text-inkSoft">
            Change a number, hit save, and the booking calculator plus the website price cards update right away.
          </p>
        </div>
      </div>

      {services === null && (
        <p className="py-10 text-center font-mono text-xs uppercase tracking-[0.2em] text-inkSoft">Loading rates…</p>
      )}
      {services?.length === 0 && (
        <p className="py-10 text-center text-sm text-inkSoft">No services found — please refresh.</p>
      )}

      <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-2">
        {services?.map((svc) => (
          <div
            key={svc.slug}
            data-testid={`admin-rates-${svc.slug}`}
            className="rounded-2xl border border-line bg-cream/60 p-5"
          >
            <div className="flex items-center justify-between gap-3">
              <h4 className="font-display text-xl font-medium text-ink">{svc.name}</h4>
              <button
                onClick={() => save(svc)}
                disabled={saving === svc.slug}
                data-testid={`admin-rates-save-${svc.slug}`}
                className="inline-flex items-center gap-2 rounded-full bg-forest px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.18em] text-cream transition-colors duration-200 hover:bg-ink disabled:opacity-60"
              >
                <Save className="h-3.5 w-3.5" />
                {saving === svc.slug ? "Saving…" : "Save"}
              </button>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              {fieldsFor(svc).map(([key, label]) => (
                <label key={key} className="block">
                  <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkSoft">{label}</span>
                  <span className="mt-1 flex items-center gap-1 rounded-2xl border border-line bg-white px-3 transition-[border-color] duration-200 focus-within:border-marmalade">
                    <span className="text-sm text-inkSoft">$</span>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      inputMode="numeric"
                      value={svc.rates[key] ?? ""}
                      onChange={(e) => setRate(svc.slug, key, e.target.value)}
                      data-testid={`admin-rate-${svc.slug}-${key}`}
                      className="w-full bg-transparent py-2.5 text-sm text-ink focus:outline-none"
                    />
                  </span>
                </label>
              ))}
            </div>
            <label className="mt-4 block">
              <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkSoft">
                Price card line (shown on the website)
              </span>
              <input
                type="text"
                value={svc.price || ""}
                onChange={(e) => setPrice(svc.slug, e.target.value)}
                data-testid={`admin-rate-price-${svc.slug}`}
                placeholder="e.g. Dogs $49 / night · Cats $36"
                className="mt-1 w-full rounded-2xl border border-line bg-white px-4 py-2.5 text-sm text-ink transition-[border-color] duration-200 focus:border-marmalade focus:outline-none"
              />
            </label>
          </div>
        ))}
      </div>
    </section>
  );
};

export default AdminRates;
